<template>
    <div :class="['group', cls, formClass, {'has-left-icon': leftIcon, 'has-right-icon': rightIcon}]" :id="'groupId' + computedId">
        <!-- Left Icon -->
        <div v-if="$slots.leftIcon" class="left-icon">
            <slot name="leftIcon"></slot>
        </div>
        <div class="left-icon" v-else-if="leftIcon">
            <i :class="['fi', leftIcon]"></i>
        </div>

        <!-- Textarea -->
        <textarea
            v-if="type === 'textarea'"
            :class="[controlClass, {'error': localError}]"
            :id="computedId"
            :rows="rows"
            :cols="cols"
            :required="required"
            :disabled="disabled"
            :name="name"
            :placeholder="placeholder ?? label"
            v-model="model"
            @input="handleInput"
            :aria-describedby="computedId + 'Help'"
        ></textarea>

        <!-- Switch -->
        <input
            v-else-if="type === 'switch'"
            :value="model"
            :checked="model"
            :class="[controlClass, {'error': localError}]"
            :id="computedId"
            type="checkbox"
            :required="required"
            :disabled="disabled"
            :name="name"
            @change="handleInput"
            :aria-describedby="computedId + 'Help'"
            :role="type"
        />

        <!-- Radio -->
        <input
            v-else-if="type === 'radio'"
            :type="type"
            :value="value"
            :checked="checked"
            :class="[controlClass, {'error': localError}]"
            :id="computedId"
            :required="required"
            :disabled="disabled"
            :name="name"
            @change="handleInput"
            :aria-describedby="computedId + 'Help'"
            :role="type"
        />

        <!-- Input field -->
        <input
            v-else
            :type="type"
            :value="value"
            :class="[controlClass, {'error': localError}]"
            :id="computedId"
            :required="required"
            :disabled="disabled"
            :name="name"
            :placeholder="placeholder ?? label"
            @input="handleInput"
            :aria-describedby="computedId + 'Help'"
            :autocomplete="autocomplete"
            :min="min"
            :max="max"
        />

        <!-- Right Icon -->
        <div v-if="$slots.rightIcon" class="right-icon">
            <slot name="rightIcon"></slot>
        </div>
        <div class="right-icon" v-else-if="help" data-bs-toggle="tooltip" data-bs-placement="top" :title="help">
            <i class="fi fi-rr-interrogation"></i>
        </div>
        <div class="right-icon" v-else-if="rightIcon">
            <i :class="['fi', rightIcon]"></i>
        </div>

        <!-- Label -->
        <label v-if="type !== 'hidden' && label?.length" :for="computedId">{{ label }} <span v-if="required">*</span></label>

        <!-- Error Feedback -->
        <div class="invalid-feedback d-block" v-if="localError && showErrorMessage">
            {{ localError }}
        </div>
    </div>
</template>

<script>
import { slugify } from "../../services/Defaults";
import AbstractAtom from "./AbstractAtom.vue";

export default {
    name: "BasicInput",
    extends: AbstractAtom,
    props: {
        value: [String, Number, Boolean, Array],
        modelValue: String,
        id: String,
        name: String,
        label: String,
        placeholder: String,
        autocomplete: {
            type: String,
            default: 'off'
        },
        type: {
            type: String,
            default: 'text'
        },
        checked: {
            type: Boolean,
            default: false
        },
        response: {
            type: String,
            default: 'auto'
        },
        rows: {
            type: [String, Number],
            default: '3'
        },
        cols: {
            type: [String, Number],
            default: '3'
        },
        required: {
            type: Boolean,
            default: false
        },
        disabled: {
            type: Boolean,
            default: false
        },
        help: String,
        error: String,
        showErrorMessage: {
            type: Boolean,
            default: true
        },
        min: Number,
        max: Number,
        leftIcon: String,
        rightIcon: String,
        cls: String
    },
    data() {
        return {
            model: this.value,
            localError: this.error
        };
    },
    computed: {
        computedId() {
            return this.id ?? slugify(this.label ?? this.name ?? this.placeholder ?? 'custom');
        },
        formClass() {
            switch (this.type) {
                case 'switch':
                    return 'form-check form-switch';
                case 'checkbox':
                case 'radio':
                    return 'form-check';
                default:
                    return this.label ? 'form-floating' : '';
            }
        },
        controlClass() {
            return ['checkbox', 'radio', 'switch'].includes(this.type) ? 'form-check-input' : 'form-control';
        },
    },
    watch: {
        value(val) {
            this.model = val;
        },
        error(val) {
            this.localError = val;
        }
    },
    methods: {
        handleInput(event) {
            let value;

            if (this.type === 'switch') {
                if(this.response === 'number') {
                    value = event.target.checked ? 1 : 0;
                } else {
                    value = event.target.checked;
                }
            } else {
                value = event.target.value;
            }

            this.$emit('input', value);
            this.$emit('change', value);
            this.$emit('reset-error');
        },
        initTooltip() {
            if(this.help) {
                setTimeout(() => {
                    const tooltipTriggerList = [].slice.call(document.querySelectorAll(`#groupId${this.computedId} [data-bs-toggle="tooltip"]`));
                    tooltipTriggerList.map(tooltipTriggerEl => new bootstrap.Tooltip(tooltipTriggerEl));
                }, 300);
            }

        },
    },
    mounted() {
        this.initTooltip();
    }
};
</script>
